/**
 * BPMN Canvas Panel
 *
 * Context-aware BPMN viewer: Project-level BPMN when no artifact selected,
 * Artifact-level BPMN when artifact selected
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { Button } from '../ui/Button';
import { Save, ArrowLeft, Loader2, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { transformApi, readStream } from '@/lib/api';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { normalizeArtifactDesign } from '@/utils/artifactDesign';
import { useLumaJsonGeneration } from '@/hooks/useLumaJsonGeneration';

// Note: bpmn-js will be imported dynamically to avoid SSR issues
let BpmnModeler: any = null;

export const BPMNCanvasPanel: React.FC = () => {
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const [modelerReady, setModelerReady] = useState(false);
  const modelerRef = useRef<any>(null);
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const loadArtifacts = useProjectStore((state) => state.loadArtifacts);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const { addToast } = useToastStore();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showArtifactConfirm, setShowArtifactConfirm] = useState(false);
  const [showLumaJsonConfirm, setShowLumaJsonConfirm] = useState(false);
  const [fallbackRequest, setFallbackRequest] = useState<{ reason?: string; message?: string } | null>(null);
  const [previousContent, setPreviousContent] = useState<string>('');
  const [transforming, setTransforming] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingLumaJson, setGeneratingLumaJson] = useState(false);
  const { startLumaJsonGeneration } = useLumaJsonGeneration();

  // Get current project and artifact
  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  // Context detection
  const isProjectContext = !selectedArtifactId;

  const refreshArtifactData = useCallback(async () => {
    if (!activeProjectId) return;
    await loadArtifacts(activeProjectId);
  }, [activeProjectId, loadArtifacts]);

  // Get BPMN content based on context
  const bpmn = isProjectContext
    ? project?.stages.BPMN?.content || null
    : artifact?.stages.BPMN?.content || null;

  // Get LumaScript content based on context (needed for artifact design generation)
  const lumascript = isProjectContext
    ? project?.stages.LUMASCRIPT?.content || null
    : artifact?.stages.LUMASCRIPT?.content || null;
  const artifactLumaJson = artifact?.lumaJson;

  // Track the last BPMN content we loaded to avoid unnecessary reloads
  const lastLoadedBpmnRef = useRef<string | null>(null);

  // Callback ref - this gets called when the element is mounted/unmounted
  const containerRefCallback = useCallback((element: HTMLDivElement | null) => {
    setContainerElement(element);
  }, []);

  // EFFECT 1: Initialize modeler when container is ready AND watch for resize
  useEffect(() => {
    if (!project || !containerElement) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;

    const initModeler = async () => {
      // Dynamically import bpmn-js
      if (!BpmnModeler) {
        const module = await import('bpmn-js/lib/Modeler');
        BpmnModeler = module.default;
      }

      // Initialize modeler with the container element we know exists
      const modeler = new BpmnModeler({
        container: containerElement,
        keyboard: {
          bindTo: document
        }
      });

      modelerRef.current = modeler;
      lastLoadedBpmnRef.current = null; // Reset for new artifact

      // Listen for changes
      modeler.on('commandStack.changed', () => {
        modeler.saveXML({ format: true }, (err: any, xml: string) => {
          if (!err && activeProjectId) {
            if (isProjectContext) {
              updateProjectStage(activeProjectId, 'BPMN', xml, true);
            } else if (selectedArtifactId) {
              updateArtifactStage(activeProjectId, selectedArtifactId, 'BPMN', xml, true);
            }
          }
        });
      });

      // CRITICAL: Watch for container size changes
      // When container gets dimensions, trigger canvas resize
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;

          if (width > 0 && height > 0 && modelerRef.current) {
            // Container has dimensions - trigger canvas update
            try {
              const canvas = modelerRef.current.get('canvas');
              canvas.resized();
              // Also re-fit viewport if we have BPMN loaded
              if (lastLoadedBpmnRef.current) {
                canvas.zoom('fit-viewport');
              }
            } catch (error) {
              console.error('[BPMNCanvas] Error resizing canvas:', error);
            }
          }
        }
      });

      resizeObserver.observe(containerElement);

      // CRITICAL: Give bpmn-js time to fully initialize its DOM
      await new Promise(resolve => setTimeout(resolve, 100));

      // Signal that modeler is ready - this will trigger the second effect
      setModelerReady(true);
    };

    // Reset ready state first
    setModelerReady(false);
    initModeler();

    // Cleanup
    return () => {
      setModelerReady(false);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (modelerRef.current) {
        modelerRef.current.destroy();
        modelerRef.current = null;
      }
      lastLoadedBpmnRef.current = null;
    };
  }, [selectedArtifactId, containerElement]); // Only re-init when artifact or container changes, NOT bpmn

  // EFFECT 2: Load BPMN data when modeler is ready and we have data
  useEffect(() => {
    if (!modelerReady || !bpmn || bpmn === lastLoadedBpmnRef.current) {
      return;
    }

    const loadBpmn = async () => {
      try {
        const modeler = modelerRef.current!;
        await modeler.importXML(bpmn);

        const canvas = modeler.get('canvas');
        canvas.zoom('fit-viewport');

        lastLoadedBpmnRef.current = bpmn;
      } catch (error: any) {
        console.error('[BPMNCanvas] ❌ BPMN import error:', error);
        lastLoadedBpmnRef.current = null; // Reset so we can retry
      }
    };

    // No delay needed here - modeler is already fully initialized
    loadBpmn();
  }, [modelerReady, bpmn, containerElement]); // Re-import when modeler becomes ready or bpmn data changes

  const handleSave = async () => {
    if (!modelerRef.current || !activeProjectId || !selectedArtifactId) return;

    try {
      const result = await modelerRef.current.saveXML({ format: true });
      await updateArtifactStage(activeProjectId, selectedArtifactId, 'BPMN', result.xml, false);
      try {
        await refreshArtifactData();
      } catch (refreshError) {
        console.warn('[BPMNCanvas] Failed to refresh artifacts after BPMN save', refreshError);
      }
      addToast('success', 'BPMN diagram saved');
    } catch (error) {
      console.error('Save error:', error);
      addToast('error', 'Failed to save BPMN diagram');
    }
  };

  const handleToLumaScriptClick = () => {
    const lumascriptStage = artifact?.stages.LUMASCRIPT;
    if (lumascriptStage && lumascriptStage.content.trim()) {
      setShowConfirm(true);
    } else {
      executeToLumaScript();
    }
  };

  const executeToLumaScript = async () => {
    if (!bpmn || !activeProjectId || !selectedArtifactId) return;

    const lumascriptStage = artifact?.stages.LUMASCRIPT;
    setPreviousContent(lumascriptStage?.content || '');
    setTransforming(true);

    try {
      const stream = await transformApi.bpmnToLumaScript(bpmn);
      let lumaScriptJson = '';
      for await (const chunk of readStream(stream)) {
        lumaScriptJson += chunk;
      }
      // Validate JSON before storing
      JSON.parse(lumaScriptJson); // Will throw if invalid
      updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', lumaScriptJson, false);
      setActiveStage('LUMASCRIPT');
      addToast('success', 'Successfully transformed BPMN to LumaScript');
    } catch (error: any) {
      console.error('Transform error:', error);
      addToast('error', `Failed to transform: ${error.message || 'Unknown error'}`);
      // Restore previous content on error
      updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', previousContent, false);
    } finally {
      setTransforming(false);
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    executeToLumaScript();
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const executeArtifactDesign = async () => {
    if (!activeProjectId || !project) return;

    const lumascriptContent = isProjectContext
      ? project.stages.LUMASCRIPT?.content
      : artifact?.stages.LUMASCRIPT?.content;

    if (!lumascriptContent) {
      addToast('error', 'No LumaScript found. Generate LumaScript first.');
      return;
    }

    setAnalyzing(true);
    try {
      const lumascriptObj = JSON.parse(lumascriptContent);
      const stream = await transformApi.lumaScriptToArtifactDesign(lumascriptObj, project.suffix);

      let artifactDesignJson = '';
      for await (const chunk of readStream(stream)) {
        artifactDesignJson += chunk;
      }

      const artifactDesign = JSON.parse(artifactDesignJson);
      const normalizedDesign = normalizeArtifactDesign(artifactDesign, project.suffix ?? artifactDesign.projectSuffix ?? '');
      const normalizedJson = JSON.stringify(normalizedDesign);

      if (isProjectContext) {
        updateProjectStage(activeProjectId, 'ARTIFACT_DESIGN', normalizedJson, false);
      } else if (selectedArtifactId) {
        updateArtifactStage(activeProjectId, selectedArtifactId, 'ARTIFACT_DESIGN', normalizedJson, false);
      }

      setActiveStage('ARTIFACT_DESIGN');
      addToast('success', 'Artifact analysis complete');
    } catch (error: any) {
      console.error('Artifact design analysis error:', error);
      addToast('error', `Failed to analyze: ${error.message || 'Unknown error'}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleArtifactConfirm = () => {
    setShowArtifactConfirm(false);
    executeArtifactDesign();
  };

  const handleArtifactCancel = () => {
    setShowArtifactConfirm(false);
  };

  const handleToArtifactDesignClick = () => {
    if (!activeProjectId || !project) return;

    const existingArtifactDesign = isProjectContext
      ? project.stages.ARTIFACT_DESIGN?.content
      : artifact?.stages?.ARTIFACT_DESIGN?.content;

    if (existingArtifactDesign && existingArtifactDesign.trim().length > 0) {
      setShowArtifactConfirm(true);
      return;
    }

    executeArtifactDesign();
  };

  const ensureLumaJsonPrereqs = (): boolean => {
    if (!artifact) {
      addToast('error', 'Select an artifact before generating LumaJSON.');
      return false;
    }

    const lumaScriptStage = artifact.stages?.LUMASCRIPT;
    const bpmnStage = artifact.stages?.BPMN;

    if (!lumaScriptStage?.content) {
      addToast('error', 'Generate and save LumaScript before creating LumaJSON.');
      return false;
    }

    if (!bpmnStage?.content) {
      addToast('error', 'Generate and save BPMN before creating LumaJSON.');
      return false;
    }

    if (lumaScriptStage.isDirty || bpmnStage.isDirty) {
      addToast('warning', 'Save LumaScript and BPMN changes before generating LumaJSON.');
      return false;
    }

    return true;
  };

  const executeLumaJsonGeneration = async (options: { useFallback?: boolean } = {}) => {
    if (!activeProjectId || !selectedArtifactId || !artifact) return;
    if (!ensureLumaJsonPrereqs()) return;

    setGeneratingLumaJson(true);
    try {
      const result = await startLumaJsonGeneration({
        artifactId: selectedArtifactId,
        artifactName: artifact.name,
        artifactType: artifact.type,
        projectId: project?.id || null,
        onRefresh: refreshArtifactData,
        useFallback: options.useFallback
      });

      if (!result) {
        return;
      }

      if (result.status === 'fallback-required') {
        setFallbackRequest({
          reason: result.fallbackReason,
          message: result.fallbackMessage
        });
        return;
      }

      if (result.status === 'completed') {
        setActiveStage('LUMAJSON');
      }
    } finally {
      setGeneratingLumaJson(false);
    }
  };

  const handleToLumaJsonClick = async () => {
    if (!activeProjectId || !selectedArtifactId || !artifact) return;

    if (!ensureLumaJsonPrereqs()) {
      return;
    }

    if (artifactLumaJson) {
      setShowLumaJsonConfirm(true);
      return;
    }

    await executeLumaJsonGeneration();
  };

  const handleLumaJsonConfirm = async () => {
    setShowLumaJsonConfirm(false);
    await executeLumaJsonGeneration();
  };

  const handleLumaJsonCancel = () => {
    setShowLumaJsonConfirm(false);
    setActiveStage('LUMAJSON');
  };

  const handleFallbackConfirm = async () => {
    setFallbackRequest(null);
    await executeLumaJsonGeneration({ useFallback: true });
  };

  const handleFallbackCancel = () => {
    setFallbackRequest(null);
  };

  const handleZoomIn = () => {
    if (!modelerRef.current) return;
    try {
      const canvas = modelerRef.current.get('canvas');
      const currentZoom = canvas.zoom();
      canvas.zoom(currentZoom + 0.1);
    } catch (error) {
      console.error('Zoom in error:', error);
    }
  };

  const handleZoomOut = () => {
    if (!modelerRef.current) return;
    try {
      const canvas = modelerRef.current.get('canvas');
      const currentZoom = canvas.zoom();
      canvas.zoom(Math.max(0.1, currentZoom - 0.1)); // Min zoom 10%
    } catch (error) {
      console.error('Zoom out error:', error);
    }
  };

  const handleZoomFit = () => {
    if (!modelerRef.current) return;
    try {
      const canvas = modelerRef.current.get('canvas');
      canvas.zoom('fit-viewport');
    } catch (error) {
      console.error('Zoom fit error:', error);
    }
  };

  // Note: This panel now works in both project context (no artifact selected) and artifact context
  // No early return needed - the panel handles both cases

  return (
    <div className="w-full h-full flex flex-col">
      {/* Conversion Buttons Bar */}
      <div className="h-8 border-b border-border bg-background flex items-center justify-center px-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          {transforming ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Transforming...</span>
            </div>
          ) : (
            <button
              onClick={handleToLumaScriptClick}
              disabled={!bpmn || transforming}
              className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3 h-3" />
              To LumaScript
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isProjectContext ? (
            analyzing ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Analyzing...</span>
              </div>
            ) : (
              <button
                onClick={handleToArtifactDesignClick}
                disabled={!lumascript || analyzing}
                className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                To Artifact Generation
                <ArrowLeft className="w-3 h-3 rotate-180" />
              </button>
            )
          ) : (
            <button
              onClick={handleToLumaJsonClick}
              disabled={!artifact || generatingLumaJson}
              className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {generatingLumaJson ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  To LumaJSON
                  <ArrowLeft className="w-3 h-3 rotate-180" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 relative" style={{ minHeight: 0 }}>
        <div
          ref={containerRefCallback}
          className="w-full h-full"
        />

        {/* Toolbar */}
        <div className="absolute top-4 right-4 flex gap-2" style={{ zIndex: 10 }}>
          {/* Zoom Controls */}
          <div className="flex gap-1 bg-background border border-border rounded-md overflow-hidden">
            <button
              onClick={handleZoomIn}
              disabled={!modelerReady || !bpmn}
              className="px-2 py-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomOut}
              disabled={!modelerReady || !bpmn}
              className="px-2 py-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-x border-border"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomFit}
              disabled={!modelerReady || !bpmn}
              className="px-2 py-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Fit to Viewport"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          {/* Save Button */}
          <Button size="sm" onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        </div>

        {!bpmn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 10 }}>
            <div className="text-center text-muted-foreground">
              <p className="text-sm mb-2">No BPMN diagram yet</p>
              <p className="text-xs">Transform from LumaScript to see the visual flow</p>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="Overwrite Existing Content?"
        message="This will overwrite the existing LumaScript content. Are you sure?"
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {isProjectContext && (
        <ConfirmDialog
          isOpen={showArtifactConfirm}
          title="Overwrite Artifact Generation?"
          message="This will overwrite the existing Artifact Generation analysis. Continue?"
          confirmLabel="Overwrite"
          cancelLabel="Cancel"
          onConfirm={handleArtifactConfirm}
          onCancel={handleArtifactCancel}
        />
      )}

      {!isProjectContext && (
        <ConfirmDialog
          isOpen={showLumaJsonConfirm}
          title="Overwrite existing LumaJSON?"
          message="This artifact already has LumaJSON. Overwrite it with a fresh AI-generated version?"
          confirmLabel="Overwrite"
          cancelLabel="View existing"
          onConfirm={handleLumaJsonConfirm}
          onCancel={handleLumaJsonCancel}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(fallbackRequest)}
        title="Use deterministic fallback?"
        message={fallbackRequest?.message
          ? `${fallbackRequest.message}${fallbackRequest.reason ? `\n\nReason: ${fallbackRequest.reason}.` : ''}\n\nWould you like to generate LumaJSON using the deterministic transformer instead?`
          : 'The LLM output could not be validated. Generate LumaJSON using the deterministic transformer instead?'}
        confirmLabel="Use Fallback"
        cancelLabel="Cancel"
        onConfirm={handleFallbackConfirm}
        onCancel={handleFallbackCancel}
      />
    </div>
  );
};
